/**
 * Die Hülle.
 *
 * Am Schreibtisch drei Spalten (ADR 0011): **Symbolleiste** mit den Bereichen,
 * **Kontext-Sidebar** (im Bereich *Objekte* der Objektbaum), Inhalt. Am Telefon
 * keine davon, dafür eine Tab-Leiste mit **drei** Zielen — dieselben drei wie in
 * der iOS-App, damit eine Journey beide Clients beschreiben kann.
 *
 * Hier stand bis ADR 0011 „es gibt genau drei Bildschirme". Die Regel ist nicht
 * aufgeweicht, sondern präzisiert: Es gibt weiterhin nur **zwei Bauformen** —
 * die Objektansicht mit fünf Reitern und die Arbeitsfläche. Neue Domänen
 * erzeugen einen Reiter darin, keinen Bildschirm. Verschieden ist nur, wie viele
 * Einstiege dorthin führen: auf iOS drei Ziele, im Web sechs Bereiche.
 * Portierbar bleibt es, weil die Bauform portiert wird und nicht die Hülle.
 *
 * Die Sidebar selbst kennt `AppShell` nicht mehr — sie kommt als `tree`-Slot
 * vom Aufrufer (`router.tsx`). Grund: `AppShell` liegt in `components/`, und
 * `components/` darf `features/` nicht importieren (eslint-boundaries). Seit
 * Feature 01 zeigt die Sidebar den Objektbaum, seit Spec 02a
 * `features/objects/ObjectTree` (zuvor `features/properties/PropertyTree`);
 * verdrahtet wird das an der Wurzelroute, die als `app`-Ebene beides
 * importieren darf.
 */

import { Link, useRouterState } from '@tanstack/react-router'
import type { ReactNode } from 'react'

// `nav-today` bleibt als Bezeichner unverändert (Spec 07, ADR 0017 Punkt 10:
// „nav-today bleibt unverändert, die Beschriftung wird ‚Kalender'") — nur die
// Beschriftung folgt der Umbenennung von „Heute" zu „Kalender".
const TABS = [
  { to: '/', label: 'Kalender', testId: 'nav-today' },
  { to: '/objekt', label: 'Objekte', testId: 'nav-objects' },
  { to: '/suchen', label: 'Suchen', testId: 'nav-search' },
] as const

interface AppShellProps {
  /**
   * Die Symbolleiste (ADR 0011) — Schreibtisch-only, kommt als Slot, weil
   * `components/` `features/` nicht importieren darf, die Zähler aber von dort
   * kommen. Dieselbe Aufteilung wie bei `tree`.
   */
  readonly rail: ReactNode
  readonly tree: ReactNode
  /**
   * Derselbe Kontomenü-Auslöser wie in der Symbolleiste, aber für <768px
   * (Spec 25, Designabnahme-Auflage B1): Die Symbolleiste selbst ist dort
   * `hidden` (`Rail.tsx`), also braucht das Kontomenü einen zweiten,
   * mobilen Platz — ein schmaler Streifen über jedem Bereich, kein
   * Navigationsziel (die Tab-Leiste bleibt bei drei Einträgen, Invariante 6).
   * `null` am Schreibtisch: Der Aufrufer entscheidet per
   * `useIsDesktopViewport()`, **welcher** der beiden Auslöser mountet — zwei
   * nur optisch versteckte Kopien trügen denselben `data-testid` doppelt
   * (`lib/viewport.ts`, dieselbe Lehre wie beim Objektbaum).
   */
  readonly mobileAccount: ReactNode
  readonly children: ReactNode
}

export function AppShell({ rail, tree, mobileAccount, children }: AppShellProps) {
  // `print:h-auto`/`print:overflow-visible` — ohne das bleibt der Inhalt auf
  // die Bildschirmhöhe begrenzt und der druckbare Nachweis des Mietkontos
  // (Spec 04c, „Drucken") würde am unteren Rand abgeschnitten statt über
  // mehrere Seiten zu laufen.
  return (
    <div className="bg-bg text-text flex h-dvh flex-col print:h-auto">
      <div className="border-border bg-surface flex min-h-(--row-h) items-center justify-end border-b px-(--page-x) py-1 md:hidden print:hidden">
        {mobileAccount}
      </div>
      <div className="flex min-h-0 flex-1 print:min-h-0">
        {rail}
        {tree}
        <main className="min-w-0 flex-1 overflow-y-auto pb-16 md:pb-0 print:overflow-visible print:pb-0">
          {children}
        </main>
      </div>
      <MobileTabs />
    </div>
  )
}

function MobileTabs() {
  // Die aktive Route bestimmt die Markierung. Der Akzent ist hier richtig
  // eingesetzt: aktiver Zustand, nicht Dekoration.
  const path = useRouterState({ select: (state) => state.location.pathname })

  return (
    <nav
      aria-label="Bereiche"
      data-testid="mobile-tabs"
      className="border-border bg-surface fixed inset-x-0 bottom-0 flex border-t md:hidden print:hidden"
    >
      {TABS.map((tab) => {
        const isActive = tab.to === '/' ? path === '/' : path.startsWith(tab.to)
        return (
          <Link
            key={tab.to}
            to={tab.to}
            data-testid={tab.testId}
            aria-current={isActive ? 'page' : undefined}
            className={`text-label flex flex-1 items-center justify-center transition-colors duration-(--duration-fast) ease-(--ease-out) ${
              isActive ? 'text-accent' : 'text-muted'
            }`}
          >
            {tab.label}
          </Link>
        )
      })}
    </nav>
  )
}
